import React, { Component } from "react";
import PropTypes from "prop-types";
import { Switch, Input, InputNumber, Button, message, Spin, Icon, Select } from "antd";
import moment from "moment";
import CompanyLosService from "./services/company-los-services";
import "./pricing-approval.css";

const { TextArea } = Input;

const ROI_THRESHOLD_MIN = 10;
const ROI_THRESHOLD_MAX = 36;
const TENURE_MIN_MONTHS = 3;
const TENURE_MAX_MONTHS = 36;
const PF_PREC_MIN = 0;
const PF_PREC_MAX = 5;
const PF_FIXED_MIN = 0;
const PF_FIXED_MAX = 500000;

const parseOptionalFloat = (str) => {
    if (str === "" || str == null) return null;
    const n = parseFloat(str);
    return Number.isNaN(n) ? null : n;
};

const parseOptionalInt = (str) => {
    if (str === "" || str == null) return null;
    const n = parseInt(str, 10);
    return Number.isNaN(n) ? null : n;
};

class PricingApproval extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            saving: false,
            enabled: false,
            roi_threshold: "",
            pf_prec_threshold: "",
            pf_fixed_threshold: "",
            min_tenure: "",
            max_tenure: "",
            pricingSchemeRows: [],
            lastPricingSchemeDetailsFromApi: [],
            schemeOptionsByKey: {},
            schemeLoadingKey: null,
            remarks: "",
            approval_log: [],
        };
    }

    componentDidMount() {
        this.fetchPricingDetails();
    }

    componentDidUpdate(prevProps) {
        const { anchorList } = this.props;
        if (anchorList === prevProps.anchorList) return;
        const n = (anchorList || []).length;
        const pn = (prevProps.anchorList || []).length;
        if (n !== pn) {
            this.setState(prev => ({
                pricingSchemeRows: this.buildRowsFromAnchorsAndDetails(
                    anchorList || [],
                    prev.pricingSchemeRows,
                    prev.lastPricingSchemeDetailsFromApi
                ),
                schemeOptionsByKey: {}
            }));
        }
    }

    buildRowsFromAnchorsAndDetails(anchorList, oldRows = [], apiDetails = []) {
        const n = (anchorList || []).length;
        const detailMap = {};
        (apiDetails || []).forEach(d => {
            if (d && d.anchor_id != null) detailMap[Number(d.anchor_id)] = d;
        });
        return Array.from({ length: n }, (_, i) => {
            const old = oldRows[i] || {};
            const anchorStillValid =
                old.anchor_id != null && anchorList.some(a => Number(a.id) === Number(old.anchor_id));
            const anchor_id = anchorStillValid ? old.anchor_id : anchorList[i] && anchorList[i].id;
            const d = anchor_id != null ? detailMap[Number(anchor_id)] : null;
            return {
                anchor_id,
                monthly_scheme_id:
                    d && d.monthly_scheme_id != null
                        ? d.monthly_scheme_id
                        : anchorStillValid
                            ? old.monthly_scheme_id
                            : undefined,
                fortnightly_scheme_id:
                    d && d.fortnightly_scheme_id != null
                        ? d.fortnightly_scheme_id
                        : anchorStillValid
                            ? old.fortnightly_scheme_id
                            : undefined
            };
        });
    }

    normalizeSchemeResponse(data) {
        const raw = Array.isArray(data) ? data : (data && (data.results || data.data)) || [];
        return raw.map(s => ({
            id: s.id,
            name: s.name != null && s.name !== "" ? s.name : `Scheme ${s.id}`
        }));
    }

    fetchPricingDetails = () => {
        this.setState({ loading: true });
        CompanyLosService.getSellerPricingDetails(this.props.companyId)
            .then(res => {
                const data = res.data;
                const list = this.props.anchorList || [];
                const apiDetails = data.pricing_scheme_details || [];
                this.setState({
                    loading: false,
                    enabled: data.pricing_approved_enabled || false,
                    roi_threshold: data.roi_threshold || "",
                    pf_prec_threshold: data.pf_prec_threshold || data.pf_prec_threshold || "",
                    pf_fixed_threshold: data.pf_fixed_threshold || "",
                    min_tenure: data.min_tenure != null ? String(data.min_tenure) : "",
                    max_tenure: data.max_tenure != null ? String(data.max_tenure) : "",
                    lastPricingSchemeDetailsFromApi: apiDetails,
                    pricingSchemeRows: this.buildRowsFromAnchorsAndDetails(list, [], apiDetails),
                    schemeOptionsByKey: {},
                    remarks: data.remarks || "",
                    approval_log: data.approval_log || [],
                });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    };

    validatePricingThresholdsOrMessage() {
        const roi = parseOptionalFloat(this.state.roi_threshold);
        if (roi != null && (roi < ROI_THRESHOLD_MIN || roi > ROI_THRESHOLD_MAX)) {
            return `ROI Threshold (%) must be between ${ROI_THRESHOLD_MIN} and ${ROI_THRESHOLD_MAX} (inclusive)`;
        }
        const pfP = parseOptionalFloat(this.state.pf_prec_threshold);
        if (pfP != null && (pfP < PF_PREC_MIN || pfP > PF_PREC_MAX)) {
            return `PF Percentage Threshold (%) must be between ${PF_PREC_MIN} and ${PF_PREC_MAX} (inclusive)`;
        }
        const pfF = parseOptionalFloat(this.state.pf_fixed_threshold);
        if (pfF != null && (pfF < PF_FIXED_MIN || pfF > PF_FIXED_MAX)) {
            return `PF Fixed Threshold (₹) must be between ${PF_FIXED_MIN} and 5,00,000 (inclusive)`;
        }
        const minT = parseOptionalInt(this.state.min_tenure);
        const maxT = parseOptionalInt(this.state.max_tenure);
        if (minT != null && (minT < TENURE_MIN_MONTHS || minT > TENURE_MAX_MONTHS)) {
            return `Min Tenure (months) must be between ${TENURE_MIN_MONTHS} and ${TENURE_MAX_MONTHS} (inclusive)`;
        }
        if (maxT != null && (maxT < TENURE_MIN_MONTHS || maxT > TENURE_MAX_MONTHS)) {
            return `Max Tenure (months) must be between ${TENURE_MIN_MONTHS} and ${TENURE_MAX_MONTHS} (inclusive)`;
        }
        if (minT != null && maxT != null && minT >= maxT) {
            return "Min Tenure should be less than Max Tenure";
        }
        return null;
    }

    validatePricingSchemeRows() {
        const { anchorList } = this.props;
        const rows = this.state.pricingSchemeRows;
        const n = (anchorList || []).length;
        if (n === 0) return true;
        const ids = rows.map(r => r.anchor_id).filter(x => x != null);
        if (ids.length !== rows.length) {
            message.error("Please select an anchor for each row");
            return false;
        }
        if (new Set(ids.map(Number)).size !== ids.length) {
            message.error("Each row must have a distinct anchor");
            return false;
        }
        return true;
    }

    buildBasePayload() {
        const minT = this.state.min_tenure ? Number(this.state.min_tenure) : null;
        const maxT = this.state.max_tenure ? Number(this.state.max_tenure) : null;
        const pricing_scheme_details = this.state.pricingSchemeRows
            .filter(r => r.anchor_id != null)
            .map(r => ({
                anchor_id: r.anchor_id,
                monthly_scheme_id: r.monthly_scheme_id != null ? Number(r.monthly_scheme_id) : null,
                fortnightly_scheme_id: r.fortnightly_scheme_id != null ? Number(r.fortnightly_scheme_id) : null,
            }));
        return {
            pricing_approved_enabled: this.state.enabled,
            roi_threshold: this.state.roi_threshold || null,
            pf_prec_threshold: this.state.pf_prec_threshold || null,
            pf_fixed_threshold: this.state.pf_fixed_threshold || null,
            min_tenure: minT,
            max_tenure: maxT,
            remarks: this.state.remarks || "",
            pricing_scheme_details,
        };
    }

    handleToggle = (checked) => {
        if (checked) {
            const thresholdErr = this.validatePricingThresholdsOrMessage();
            if (thresholdErr) {
                return message.error(thresholdErr);
            }
            if (!this.validatePricingSchemeRows()) return;
        }
        this.setState({ saving: true });
        const payload = {
            ...this.buildBasePayload(),
            pricing_approved_enabled: checked,
            remarks: this.state.remarks || "",
        };
        CompanyLosService.updateSellerPricingDetails(this.props.companyId, payload)
            .then(() => {
                this.setState({ enabled: checked, saving: false });
                message.success(`Pricing Approval ${checked ? "enabled" : "disabled"}`);
                this.fetchPricingDetails();
            })
            .catch(() => {
                this.setState({ saving: false });
                message.error("Failed to update Pricing Approval status");
            });
    };

    handleSave = () => {
        const thresholdErr = this.validatePricingThresholdsOrMessage();
        if (thresholdErr) {
            return message.error(thresholdErr);
        }
        if (!this.validatePricingSchemeRows()) return;
        this.setState({ saving: true });
        const payload = {
            ...this.buildBasePayload(),
            pricing_approved_enabled: this.state.enabled,
            remarks: this.state.remarks,
        };
        CompanyLosService.updateSellerPricingDetails(this.props.companyId, payload)
            .then(() => {
                this.setState({ saving: false });
                message.success("Pricing Approval settings saved");
                this.fetchPricingDetails();
            })
            .catch(() => {
                this.setState({ saving: false });
                message.error("Failed to save Pricing Approval settings");
            });
    };

    handleFieldChange = (field, value) => {
        if (field === "roi_threshold") {
            if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
            const num = parseFloat(value);
            if (!isNaN(num) && num > ROI_THRESHOLD_MAX) return;
        }
        if (field === "pf_prec_threshold") {
            if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
        }
        if (field === "pf_fixed_threshold") {
            if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
            const num = parseFloat(value);
            if (!isNaN(num) && num > PF_FIXED_MAX) return;
        }
        if (field === "min_tenure" || field === "max_tenure") {
            if (value !== "" && !/^\d*$/.test(value)) return;
            const ti = parseInt(value, 10);
            if (value !== "" && !Number.isNaN(ti) && ti > TENURE_MAX_MONTHS) return;
        }
        this.setState({ [field]: value });
    };

    loadSchemeOptions = (rowIndex, termLoanType) => {
        const row = this.state.pricingSchemeRows[rowIndex];
        if (!row || row.anchor_id == null) {
            message.warning("Select an anchor first");
            return;
        }
        const key = `${rowIndex}-${termLoanType}`;
        const dedupeKey = `${this.props.companyId}-${row.anchor_id}-${termLoanType}`;
        if (!this._schemeLoadInflight) this._schemeLoadInflight = new Set();
        if (this._schemeLoadInflight.has(dedupeKey)) {
            return;
        }
        this._schemeLoadInflight.add(dedupeKey);
        this.setState({ schemeLoadingKey: key });
        CompanyLosService.getSchemeListForSellerPricing(this.props.companyId, {
            term_loan_type: termLoanType,
            anchor_id: row.anchor_id
        })
            .then(({ data }) => {
                const options = this.normalizeSchemeResponse(data);
                this.setState(prev => ({
                    schemeOptionsByKey: { ...prev.schemeOptionsByKey, [key]: options },
                    schemeLoadingKey: null
                }));
            })
            .catch(() => {
                message.error("Failed to load schemes");
                this.setState({ schemeLoadingKey: null });
            })
            .then(() => {
                this._schemeLoadInflight.delete(dedupeKey);
            });
    };

    onRowAnchorChange = (rowIndex, anchorId) => {
        this.setState(prev => {
            const rows = prev.pricingSchemeRows.map((r, i) =>
                i === rowIndex
                    ? { ...r, anchor_id: anchorId, monthly_scheme_id: undefined, fortnightly_scheme_id: undefined }
                    : r
            );
            const nextOpts = { ...prev.schemeOptionsByKey };
            delete nextOpts[`${rowIndex}-monthly`];
            delete nextOpts[`${rowIndex}-fortnightly`];
            return { pricingSchemeRows: rows, schemeOptionsByKey: nextOpts };
        });
    };

    onRowSchemeChange = (rowIndex, field, value) => {
        this.setState(prev => {
            const rows = prev.pricingSchemeRows.map((r, i) =>
                i === rowIndex ? { ...r, [field]: value } : r
            );
            return { pricingSchemeRows: rows };
        });
    };

    getAnchorsTakenExcept(rowIndex) {
        const taken = new Set();
        this.state.pricingSchemeRows.forEach((r, i) => {
            if (i !== rowIndex && r.anchor_id != null) taken.add(Number(r.anchor_id));
        });
        return taken;
    }

    renderAnchorAndSchemeRows(disabled) {
        const { anchorList } = this.props;
        const rows = this.state.pricingSchemeRows;
        if (!anchorList || anchorList.length === 0) return null;

        return (
            <div className="pa-scheme-rows">
                {rows.map((row, rowIndex) => {
                    const taken = this.getAnchorsTakenExcept(rowIndex);
                    const singleAnchor = anchorList.length === 1;
                    return (
                        <div className="d-flex pa-field-row pa-scheme-row" key={rowIndex}>
                            <div className="pa-field-col">
                                <label className="pa-label fw-600">Anchor</label>
                                {singleAnchor ? (
                                    <InputNumber
                                        style={{ width: "30%", minWidth: 120 }}
                                        value={anchorList[0].short_name}
                                        disabled
                                    />
                                ) : (
                                    <Select
                                        showSearch
                                        style={{ width: 200, maxWidth: "100%" }}
                                        placeholder="Select Anchor"
                                        optionFilterProp="children"
                                        value={row.anchor_id}
                                        onChange={val => this.onRowAnchorChange(rowIndex, val)}
                                        disabled={disabled}
                                        filterOption={(input, option) =>
                                            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {anchorList.map((item, k) => (
                                            <Select.Option
                                                value={item.id}
                                                key={k}
                                                disabled={
                                                    taken.has(Number(item.id)) &&
                                                    Number(item.id) !== Number(row.anchor_id)
                                                }
                                            >
                                                {item.short_name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                )}
                            </div>
                            <div className="pa-field-col">
                                <label className="pa-label fw-600">Monthly scheme</label>
                                <Select
                                    showSearch
                                    allowClear
                                    style={{ width: 200, maxWidth: "100%" }}
                                    placeholder="Select monthly scheme"
                                    optionFilterProp="children"
                                    value={row.monthly_scheme_id}
                                    onChange={val => this.onRowSchemeChange(rowIndex, "monthly_scheme_id", val)}
                                    disabled={disabled}
                                    loading={this.state.schemeLoadingKey === `${rowIndex}-monthly`}
                                    onDropdownVisibleChange={open => {
                                        if (open) this.loadSchemeOptions(rowIndex, "monthly");
                                    }}
                                    filterOption={(input, option) =>
                                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {(this.state.schemeOptionsByKey[`${rowIndex}-monthly`] || []).map(s => (
                                        <Select.Option value={s.id} key={s.id}>
                                            {s.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="pa-field-col">
                                <label className="pa-label fw-600">Fortnightly scheme</label>
                                <Select
                                    showSearch
                                    allowClear
                                    style={{ width: 200, maxWidth: "100%" }}
                                    placeholder="Select fortnightly scheme"
                                    optionFilterProp="children"
                                    value={row.fortnightly_scheme_id}
                                    onChange={val => this.onRowSchemeChange(rowIndex, "fortnightly_scheme_id", val)}
                                    disabled={disabled}
                                    loading={this.state.schemeLoadingKey === `${rowIndex}-fortnightly`}
                                    onDropdownVisibleChange={open => {
                                        if (open) this.loadSchemeOptions(rowIndex, "fortnightly");
                                    }}
                                    filterOption={(input, option) =>
                                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {(this.state.schemeOptionsByKey[`${rowIndex}-fortnightly`] || []).map(s => (
                                        <Select.Option value={s.id} key={s.id}>
                                            {s.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderForm() {
        const disabled = !this.props.canEdit;
        return (
            <div className="pa-form">
                <p className="pa-description">
                    Set minimum thresholds. Pricing below these values will require approval.
                </p>
                <div className="d-flex pa-field-row">
                    <div className="pa-field-col">
                        <label className="pa-label fw-600">ROI Threshold (%)</label>
                        <Input className="pa-input" placeholder="e.g. 12.50" value={this.state.roi_threshold} onChange={e => this.handleFieldChange("roi_threshold", e.target.value)} disabled={disabled} />
                        <span className="pa-help-text">Allowed: {ROI_THRESHOLD_MIN}–{ROI_THRESHOLD_MAX}%. Approval required if ROI falls below this rate</span>
                    </div>
                    <div className="pa-field-col">
                        <label className="pa-label fw-600">PF Percentage Threshold (%)</label>
                        <Input className="pa-input" placeholder="e.g. 1.00" value={this.state.pf_prec_threshold} onChange={e => this.handleFieldChange("pf_prec_threshold", e.target.value)} disabled={disabled} />
                        <span className="pa-help-text">Allowed: {PF_PREC_MIN}–{PF_PREC_MAX}%. Approval required if PF percentage falls below this rate</span>
                    </div>
                    <div className="pa-field-col">
                        <label className="pa-label fw-600">PF Fixed Threshold (₹)</label>
                        <Input className="pa-input" placeholder="e.g. 500" value={this.state.pf_fixed_threshold} onChange={e => this.handleFieldChange("pf_fixed_threshold", e.target.value)} disabled={disabled} />
                        <span className="pa-help-text">Allowed: ₹0–₹5,00,000. Approval required if PF fixed amount falls below this value</span>
                    </div>
                </div>
                <div className="d-flex pa-field-row">
                    <div className="pa-field-col">
                        <label className="pa-label fw-600">Min Tenure (months)</label>
                        <Input className="pa-input" placeholder="e.g. 6" value={this.state.min_tenure} onChange={e => this.handleFieldChange("min_tenure", e.target.value)} disabled={disabled} />
                        <span className="pa-help-text">Allowed: {TENURE_MIN_MONTHS}–{TENURE_MAX_MONTHS} months. Approval required if tenure is below this value</span>
                    </div>
                    <div className="pa-field-col">
                        <label className="pa-label fw-600">Max Tenure (months)</label>
                        <Input className="pa-input" placeholder="e.g. 36" value={this.state.max_tenure} onChange={e => this.handleFieldChange("max_tenure", e.target.value)} disabled={disabled} />
                        <span className="pa-help-text">Allowed: {TENURE_MIN_MONTHS}–{TENURE_MAX_MONTHS} months. Approval required if tenure exceeds this value</span>
                    </div>
                </div>
                {this.renderAnchorAndSchemeRows(disabled)}
                <div className="pa-remarks">
                    <label className="pa-label fw-600">Remarks</label>
                    <TextArea className="pa-input" placeholder="Add remarks for ROI threshold..." value={this.state.remarks} onChange={e => this.handleFieldChange("remarks", e.target.value)} disabled={disabled} rows={3} />
                </div>
                {this.props.canEdit && (
                    <div className="d-flex justify-content-flex-end">
                        <Button type="primary" className="pa-save-btn" onClick={this.handleSave} loading={this.state.saving}>Save</Button>
                    </div>
                )}
            </div>
        );
    }

    renderChangeLog() {
        const { approval_log } = this.state;
        if (!approval_log || approval_log.length === 0) return null;
        return (
            <div className="pa-log-wrapper">
                <div className="d-flex align-items-center pa-log-header">
                    <Icon type="history" className="pa-log-icon" />
                    <span className="pa-log-title fw-600">Change Log</span>
                    <span className="pa-log-count">({approval_log.length})</span>
                </div>
                <div className="pa-log-scroll">
                    {approval_log.map((log, index) => {
                        return (
                            <div key={log.id || index} className="d-flex align-items-center pa-log-row">
                                <span className="pa-log-timestamp">
                                    {moment(log.create_date).format("DD MMM YYYY, hh:mm:ss A")}
                                </span>
                                <span className="pa-log-content">
                                    <span className="pa-log-user">{log.created_by}</span>{" "}
                                    {log.remarks && <span className="pa-log-remarks">{" | "}{log.remarks}</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    render() {
        if (this.state.loading) {
            return <div className="pa pa-card pa-loader"><Spin /></div>;
        }

        return (
            <div className="pa pa-card">
                <div className="d-flex justify-content-space-between align-items-center pa-header">
                    <div className="d-flex align-items-center">
                        <Icon type="safety-certificate" theme="twoTone" className="pa-header-icon" />
                        <span className="pa-header-title fw-600">Pricing Approval</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <span className="pa-status-label">
                            {this.state.enabled ? "Enabled" : "Disabled"}
                        </span>
                        <Switch checked={this.state.enabled} onChange={this.handleToggle} loading={this.state.saving} disabled={!this.props.canEdit} />
                    </div>
                </div>
                <div className="pa-body">
                    {this.state.enabled && this.renderForm()}
                    {this.renderChangeLog()}
                </div>
            </div>
        );
    }
}

PricingApproval.propTypes = {
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    canEdit: PropTypes.bool,
    anchorList: PropTypes.array,
};

PricingApproval.defaultProps = {
    canEdit: false,
    anchorList: [],
};

export default PricingApproval;
